import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  ClipboardCheck, Save, FileDown, Loader2, ArrowLeft, Plus, Trash2,
  Camera, Upload, PenTool, Eraser, CheckCircle2, AlertTriangle, Image as ImgIcon,
  Hammer, RefreshCw, Presentation,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CHANTIER = [
  { value: 'ouverture', label: 'Ouverture' },
  { value: 'remodeling', label: 'Remodeling' },
  { value: 'sav', label: 'SAV' },
];

const CHECKLIST_ITEMS = [
  ['corner_livre', 'Corner livré sur site'],
  ['corner_installe', 'Corner installé conformément au plan transmis'],
  ['mobilier_inox', 'Mobilier inox posé'],
  ['menuiserie', 'Menuiserie / habillage posé'],
  ['vitrines', 'Vitrine(s) installée(s)'],
  ['plv', 'PLV / enseigne / signalétique installée'],
  ['raccordements', 'Raccordements techniques réalisés selon périmètre convenu'],
  ['etat_general', 'État général conforme'],
  ['aucune_reserve', 'Aucune réserve constatée'],
  ['reserves_constatees', 'Réserves constatées'],
  ['photos_fin_chantier', 'Photos de fin de chantier ajoutées'],
];

const RESERVE_STATUS = [
  { value: 'mineure', label: 'Mineure', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'bloquante', label: 'Bloquante', cls: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'a_corriger', label: 'À corriger', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'corrigee', label: 'Corrigée', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

export default function PVLivraisonPage() {
  const { commandeId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [pv, setPv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Charge ou crée le PV à l'ouverture
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        let resp = await fetch(`${API}/api/pv-livraison/by-commande/${commandeId}`, { headers });
        let data = resp.ok ? await resp.json() : null;
        if (!data) {
          // Création à la volée
          const c = await fetch(`${API}/api/pv-livraison/from-commande/${commandeId}`, {
            method: 'POST', headers,
          });
          if (!c.ok) {
            toast.error("Impossible de créer le PV pour cette commande");
            navigate('/dossiers-agenceur');
            return;
          }
          data = await c.json();
        }
        setPv(data);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [commandeId]);

  const isSigned = pv?.status === 'signed';

  const set = (k, v) => setPv(p => ({ ...p, [k]: v }));
  const setChecklist = (k, v) => setPv(p => ({ ...p, checklist: { ...(p.checklist || {}), [k]: v } }));

  const save = async (silent = false) => {
    if (!pv) return;
    setSaving(true);
    try {
      const body = {
        enseigne: pv.enseigne, nom_magasin: pv.nom_magasin,
        adresse: pv.adresse, code_postal: pv.code_postal, ville: pv.ville,
        date_installation: pv.date_installation, nom_prestataire: pv.nom_prestataire,
        nom_responsable_wasabi: pv.nom_responsable_wasabi,
        nom_contact_magasin: pv.nom_contact_magasin,
        contact_magasin_email: pv.contact_magasin_email,
        reference_commande: pv.reference_commande,
        type_chantier: pv.type_chantier || '',
        checklist: pv.checklist || {},
        reserves: pv.reserves || [],
        commentaire_final: pv.commentaire_final || '',
      };
      const r = await fetch(`${API}/api/pv-livraison/${pv.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        // Auto-saves stay silent. Manual saves surface the error.
        if (!silent) toast.error(e.detail || "Erreur d'enregistrement");
        return false;
      }
      const updated = await r.json();
      setPv(updated);
      lastSavedSnapshotRef.current = JSON.stringify(body);
      setAutoSavedAt(new Date().toISOString());
      if (!silent) toast.success('PV enregistré');
      return true;
    } finally {
      setSaving(false);
    }
  };

  // ===== AUTO-SAVE BACKEND (debounced 2s, silent) =====
  // The PV is created on the server at first load (POST from-commande), so every
  // field is editable against an existing ID. Auto-save protects the user from
  // losing edits on session expiration, tab close, or network blips, without
  // requiring them to remember to click "Enregistrer".
  const lastSavedSnapshotRef = useRef(null);
  const [autoSavedAt, setAutoSavedAt] = useState(null);

  // Initialize the snapshot once the PV is first loaded so the autosave effect
  // doesn't fire on the initial render (no edits yet).
  useEffect(() => {
    if (pv && lastSavedSnapshotRef.current === null) {
      lastSavedSnapshotRef.current = JSON.stringify({
        enseigne: pv.enseigne, nom_magasin: pv.nom_magasin,
        adresse: pv.adresse, code_postal: pv.code_postal, ville: pv.ville,
        date_installation: pv.date_installation, nom_prestataire: pv.nom_prestataire,
        nom_responsable_wasabi: pv.nom_responsable_wasabi,
        nom_contact_magasin: pv.nom_contact_magasin,
        contact_magasin_email: pv.contact_magasin_email,
        reference_commande: pv.reference_commande,
        type_chantier: pv.type_chantier || '',
        checklist: pv.checklist || {},
        reserves: pv.reserves || [],
        commentaire_final: pv.commentaire_final || '',
      });
    }
  }, [pv]);

  // Debounced auto-save: compares the current editable body against the last
  // server-confirmed snapshot. Only PUTs when something actually changed and
  // when the PV is not yet signed.
  useEffect(() => {
    if (!pv || isSigned || saving) return;
    const body = {
      enseigne: pv.enseigne, nom_magasin: pv.nom_magasin,
      adresse: pv.adresse, code_postal: pv.code_postal, ville: pv.ville,
      date_installation: pv.date_installation, nom_prestataire: pv.nom_prestataire,
      nom_responsable_wasabi: pv.nom_responsable_wasabi,
      nom_contact_magasin: pv.nom_contact_magasin,
      contact_magasin_email: pv.contact_magasin_email,
      reference_commande: pv.reference_commande,
      type_chantier: pv.type_chantier || '',
      checklist: pv.checklist || {},
      reserves: pv.reserves || [],
      commentaire_final: pv.commentaire_final || '',
    };
    const snapshot = JSON.stringify(body);
    if (snapshot === lastSavedSnapshotRef.current) return;
    const handle = setTimeout(() => {
      save(true); // silent
    }, 2000);
    return () => clearTimeout(handle);
  }, [pv, isSigned, saving]); // eslint-disable-line react-hooks/exhaustive-deps

  const addReserve = () => {
    const r = { id: crypto.randomUUID(), texte: '', photo_path: null, statut: null };
    setPv(p => ({ ...p, reserves: [...(p.reserves || []), r] }));
  };
  const updateReserve = (id, patch) =>
    setPv(p => ({ ...p, reserves: (p.reserves || []).map(r => r.id === id ? { ...r, ...patch } : r) }));
  const removeReserve = (id) =>
    setPv(p => ({ ...p, reserves: (p.reserves || []).filter(r => r.id !== id) }));

  const uploadPhoto = async (file, opts = {}) => {
    // opts: { reserveId?: string, kind?: 'generale' | 'reclamation' }
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const params = new URLSearchParams();
    if (opts.reserveId) params.set('reserve_id', opts.reserveId);
    if (opts.kind) params.set('kind', opts.kind);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const r = await fetch(`${API}/api/pv-livraison/${pv.id}/photo${qs}`, {
      method: 'POST', headers, body: fd,
    });
    if (!r.ok) {
      toast.error("Échec de l'upload photo");
      return;
    }
    const d = await r.json();
    if (opts.reserveId) {
      updateReserve(opts.reserveId, { photo_path: d.path });
    } else if (opts.kind === 'reclamation') {
      setPv(p => ({ ...p, photos_reclamations: [...(p.photos_reclamations || []), d.path] }));
    } else {
      setPv(p => ({ ...p, photos_chantier: [...(p.photos_chantier || []), d.path] }));
    }
    toast.success('Photo ajoutée');
  };

  const removePhoto = async (path, kind = 'generale') => {
    const params = new URLSearchParams({ path });
    if (kind === 'reclamation') params.set('kind', 'reclamation');
    const r = await fetch(`${API}/api/pv-livraison/${pv.id}/photo?${params.toString()}`, {
      method: 'DELETE', headers,
    });
    if (!r.ok) return;
    if (kind === 'reclamation') {
      setPv(p => ({ ...p, photos_reclamations: (p.photos_reclamations || []).filter(x => x !== path) }));
    } else {
      setPv(p => ({ ...p, photos_chantier: (p.photos_chantier || []).filter(x => x !== path) }));
    }
  };

  const downloadPDF = () => {
    window.open(`${API}/api/pv-livraison/${pv.id}/pdf?token=${token}`, '_blank');
  };

  const uploadSignedScan = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${API}/api/pv-livraison/${pv.id}/upload-signed-scan`, {
      method: 'POST', headers, body: fd,
    });
    if (!r.ok) {
      toast.error("Échec de l'upload du scan");
      return;
    }
    toast.success('Scan PV signé enregistré');
    // Reload
    const resp = await fetch(`${API}/api/pv-livraison/${pv.id}`, { headers });
    if (resp.ok) setPv(await resp.json());
  };

  const [importingChantier, setImportingChantier] = useState(false);
  const importChantierPhotos = async () => {
    setImportingChantier(true);
    try {
      const r = await fetch(`${API}/api/pv-livraison/${pv.id}/import-chantier-photos`, {
        method: 'POST', headers,
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Échec de l'import des photos chantier");
        return;
      }
      const data = await r.json();
      if (data.imported_count > 0) {
        toast.success(`${data.imported_count} photo(s) importée(s) du chantier ${data.chantier_enseigne}`);
      } else if (!data.chantier_id) {
        toast.warning(data.message || "Aucun chantier Pilotage lié trouvé");
      } else {
        toast.info(data.message || "Photos déjà à jour");
      }
      // Reload PV
      const resp = await fetch(`${API}/api/pv-livraison/${pv.id}`, { headers });
      if (resp.ok) setPv(await resp.json());
    } finally {
      setImportingChantier(false);
    }
  };

  const importedSet = useMemo(
    () => new Set(pv?.photos_chantier_imported_paths || []),
    [pv?.photos_chantier_imported_paths]
  );

  if (loading || !pv) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6 space-y-6" data-testid="pv-livraison-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2 -ml-2 gap-1 text-slate-500">
              <ArrowLeft className="w-4 h-4" /> Retour
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-indigo-500" /> PV de livraison — Corner installé
            </h1>
            <p className="text-sm text-slate-500 mt-1">{pv.enseigne} · {pv.ville}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isSigned ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> PV signé
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Brouillon
              </Badge>
            )}
            {/* Silent auto-save indicator (hidden when signed) */}
            {!isSigned && (saving || autoSavedAt) && (
              <span
                className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-500 px-2"
                title={autoSavedAt ? `Dernier enregistrement automatique : ${new Date(autoSavedAt).toLocaleTimeString('fr-FR')}` : 'Enregistrement en cours…'}
                data-testid="pv-autosave-indicator"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    Enregistré automatiquement
                  </>
                )}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/guide-pv')} className="gap-1" data-testid="pv-presentation-link">
              <Presentation className="w-4 h-4" /> Présentation
            </Button>
            <Button variant="outline" size="sm" onClick={() => save()} disabled={saving || isSigned} data-testid="pv-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Enregistrer
            </Button>
            <Button size="sm" onClick={downloadPDF} className="gap-1 bg-indigo-600 hover:bg-indigo-700" data-testid="pv-download-pdf">
              <FileDown className="w-4 h-4" /> Télécharger PDF
            </Button>
          </div>
        </div>

        {/* Informations générales */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Informations générales</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Enseigne" value={pv.enseigne} onChange={v => set('enseigne', v)} disabled={isSigned} />
            <Field label="Nom du magasin" value={pv.nom_magasin} onChange={v => set('nom_magasin', v)} disabled={isSigned} />
            <Field label="Adresse complète" value={pv.adresse} onChange={v => set('adresse', v)} className="sm:col-span-2" disabled={isSigned} />
            <Field label="Code postal" value={pv.code_postal} onChange={v => set('code_postal', v)} disabled={isSigned} />
            <Field label="Ville" value={pv.ville} onChange={v => set('ville', v)} disabled={isSigned} />
            <Field label="Date d'installation" type="date" value={pv.date_installation || ''} onChange={v => set('date_installation', v)} disabled={isSigned} />
            <div>
              <Label className="text-xs text-slate-600 font-semibold">Type de chantier</Label>
              <select
                value={pv.type_chantier || ''}
                onChange={e => set('type_chantier', e.target.value)}
                disabled={isSigned}
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50"
                data-testid="pv-type-chantier"
              >
                <option value="">— Sélectionner —</option>
                {TYPE_CHANTIER.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <Field label="Nom du prestataire" value={pv.nom_prestataire} onChange={v => set('nom_prestataire', v)} disabled={isSigned} />
            <Field label="Nom du responsable Wasabi" value={pv.nom_responsable_wasabi} onChange={v => set('nom_responsable_wasabi', v)} disabled={isSigned} />
            <Field label="Nom du contact magasin" value={pv.nom_contact_magasin} onChange={v => set('nom_contact_magasin', v)} disabled={isSigned} />
            <Field label="Email contact magasin (PV signé envoyé ici)" type="email" value={pv.contact_magasin_email} onChange={v => set('contact_magasin_email', v)} disabled={isSigned} placeholder="client@enseigne.com" />
            <Field label="Référence commande corner" value={pv.reference_commande} onChange={v => set('reference_commande', v)} disabled={isSigned} />
          </CardContent>
        </Card>

        {/* Checklist */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Checklist de livraison</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {CHECKLIST_ITEMS.map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 py-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!pv.checklist?.[key]}
                  onChange={e => setChecklist(key, e.target.checked)}
                  disabled={isSigned}
                  className="w-4 h-4 accent-indigo-500"
                  data-testid={`checklist-${key}`}
                />
                <span className="text-sm text-slate-700">{label}</span>
              </label>
            ))}
          </CardContent>
        </Card>

        {/* Réserves */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Réserves / observations client ({(pv.reserves || []).length})</CardTitle>
            {!isSigned && (
              <Button size="sm" variant="outline" onClick={addReserve} className="h-8 gap-1" data-testid="pv-add-reserve">
                <Plus className="w-3.5 h-3.5" /> Ajouter une réserve
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {(pv.reserves || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">Aucune réserve</p>
            ) : (
              (pv.reserves || []).map((r, idx) => (
                <ReserveCard
                  key={r.id} idx={idx + 1} reserve={r}
                  onChange={(patch) => updateReserve(r.id, patch)}
                  onRemove={() => removeReserve(r.id)}
                  onPhotoUpload={(f) => uploadPhoto(f, { reserveId: r.id })}
                  disabled={isSigned}
                  pvId={pv.id}
                  token={token}
                />
              ))
            )}
          </CardContent>
        </Card>

        {/* Photos générales (agenceur) */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">
              <span className="text-emerald-700">📷</span> Photos générales — agenceur ({(pv.photos_chantier || []).length})
            </CardTitle>
            {!isSigned && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={importChantierPhotos}
                  disabled={importingChantier}
                  className="h-8 gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="pv-import-chantier-btn"
                  title="Récupérer les photos déjà uploadées dans Pilotage Chantier (recherche par enseigne + ville)"
                >
                  {importingChantier
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <RefreshCw className="w-3.5 h-3.5" />}
                  Importer photos Pilotage Chantier
                </Button>
                <label className="cursor-pointer">
                  <input
                    type="file" accept="image/*" capture="environment" multiple className="hidden"
                    onChange={e => { Array.from(e.target.files || []).forEach(f => uploadPhoto(f, { kind: 'generale' })); e.target.value = ''; }}
                    data-testid="pv-photo-generale-input"
                  />
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-md text-xs hover:bg-emerald-50">
                    <Camera className="w-3.5 h-3.5" /> Ajouter photo(s)
                  </span>
                </label>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 italic mb-3">
              Photos de fin de chantier prises par l'agenceur (vue d'ensemble, détails de finition, etc.)
              {(pv.photos_chantier_imported_paths || []).length > 0 && (
                <span className="ml-1 text-amber-700 not-italic font-medium">
                  · {(pv.photos_chantier_imported_paths || []).length} importée(s) de Pilotage Chantier
                </span>
              )}
            </p>
            {(pv.photos_chantier || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">Aucune photo</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {pv.photos_chantier.map(p => (
                  <PhotoThumb
                    key={p}
                    path={p}
                    pvId={pv.id}
                    token={token}
                    onDelete={() => removePhoto(p, 'generale')}
                    disabled={isSigned}
                    fromChantier={importedSet.has(p)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photos en cas de réclamations */}
        <Card className="border-orange-200">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              <span className="text-orange-600">⚠️</span> Photos en cas de réclamations ({(pv.photos_reclamations || []).length})
            </CardTitle>
            {!isSigned && (
              <label className="cursor-pointer">
                <input
                  type="file" accept="image/*" capture="environment" multiple className="hidden"
                  onChange={e => { Array.from(e.target.files || []).forEach(f => uploadPhoto(f, { kind: 'reclamation' })); e.target.value = ''; }}
                  data-testid="pv-photo-reclamation-input"
                />
                <span className="inline-flex items-center gap-1 px-3 py-1.5 border border-orange-300 text-orange-700 rounded-md text-xs hover:bg-orange-50">
                  <Camera className="w-3.5 h-3.5" /> Ajouter photo(s)
                </span>
              </label>
            )}
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 italic mb-3">
              Photos justifiant les réclamations / défauts constatés sur l'installation (zoom sur défauts, dommages, etc.)
            </p>
            {(pv.photos_reclamations || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">Aucune photo</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {pv.photos_reclamations.map(p => (
                  <PhotoThumb key={p} path={p} pvId={pv.id} token={token} onDelete={() => removePhoto(p, 'reclamation')} disabled={isSigned} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Commentaire final */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Commentaire final</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              rows={4}
              value={pv.commentaire_final || ''}
              onChange={e => set('commentaire_final', e.target.value)}
              disabled={isSigned}
              placeholder="Notes complémentaires, remarques générales, points de vigilance..."
              data-testid="pv-commentaire-final"
            />
          </CardContent>
        </Card>

        {/* Bon pour acceptation : signatures */}
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-indigo-900">Bon pour acceptation</CardTitle>
            <p className="text-xs text-slate-600 mt-1">
              Le client reconnaît avoir réceptionné le corner installé ce jour, conformément aux éléments validés
              dans la commande et aux plans transmis.
            </p>
          </CardHeader>
          <CardContent>
            <SignatureBlock pv={pv} onSigned={(updated) => setPv(updated)} onScanUpload={uploadSignedScan} />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

// ====================== Composants enfants ======================

function Field({ label, value, onChange, type = 'text', className = '', disabled = false, ...rest }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-600 font-semibold">{label}</Label>
      <Input
        type={type} value={value || ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="mt-1 disabled:bg-slate-50 disabled:text-slate-500"
        {...rest}
      />
    </div>
  );
}

function ReserveCard({ idx, reserve, onChange, onRemove, onPhotoUpload, disabled, pvId, token }) {
  return (
    <div className="border rounded-lg p-3 space-y-2 bg-white" data-testid={`reserve-${reserve.id}`}>
      <div className="flex items-center justify-between gap-2">
        <Badge variant="outline" className="text-xs">Réserve #{idx}</Badge>
        <div className="flex items-center gap-2">
          <select
            value={reserve.statut || ''}
            onChange={e => onChange({ statut: e.target.value || null })}
            disabled={disabled}
            className="border rounded-md px-2 py-1 text-xs"
            data-testid={`reserve-statut-${reserve.id}`}
          >
            <option value="">— Statut —</option>
            {RESERVE_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {!disabled && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={onRemove}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      <Textarea
        rows={2}
        value={reserve.texte || ''}
        onChange={e => onChange({ texte: e.target.value })}
        disabled={disabled}
        placeholder="Décrire la réserve..."
        className="text-sm"
      />
      <div className="flex items-center gap-2">
        {reserve.photo_path ? (
          <div className="flex items-center gap-2">
            <PhotoThumb path={reserve.photo_path} pvId={pvId} token={token} small />
            {!disabled && (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" onClick={() => onChange({ photo_path: null })}>
                Retirer
              </Button>
            )}
          </div>
        ) : !disabled && (
          <label className="cursor-pointer">
            <input
              type="file" accept="image/*" capture="environment" className="hidden"
              onChange={e => { if (e.target.files?.[0]) onPhotoUpload(e.target.files[0]); e.target.value = ''; }}
            />
            <span className="inline-flex items-center gap-1 px-2 py-1 border rounded-md text-xs hover:bg-slate-50 text-slate-600">
              <Camera className="w-3.5 h-3.5" /> Photo
            </span>
          </label>
        )}
      </div>
    </div>
  );
}

function PhotoThumb({ path, pvId, token, onDelete, disabled, small = false, fromChantier = false }) {
  const url = `${API}/api/pv-livraison/photo/${path}?token=${token}`;
  return (
    <div className={`relative group ${small ? 'w-20 h-20' : 'aspect-square'} bg-slate-100 rounded overflow-hidden border ${fromChantier ? 'ring-2 ring-amber-300' : ''}`} data-testid={fromChantier ? 'pv-photo-imported' : 'pv-photo-manual'}>
      {/* token in URL only works if endpoint accepts it; otherwise use authenticated fetch */}
      <AuthImage path={path} pvId={pvId} token={token} className="w-full h-full object-cover" />
      {fromChantier && (
        <span
          className="absolute top-1 left-1 inline-flex items-center gap-0.5 bg-amber-500/95 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded shadow"
          title="Photo importée depuis Pilotage Chantier"
        >
          <Hammer className="w-2.5 h-2.5" /> Chantier
        </span>
      )}
      {onDelete && !disabled && (
        <button
          onClick={onDelete}
          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Supprimer"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function AuthImage({ path, token, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    (async () => {
      try {
        const r = await fetch(`${API}/api/pv-livraison/photo/${path}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const blob = await r.blob();
        if (!cancelled) {
          objUrl = URL.createObjectURL(blob);
          setSrc(objUrl);
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path, token]);
  return src ? <img src={src} alt="" className={className} /> : (
    <div className={`${className} flex items-center justify-center bg-slate-100`}>
      <ImgIcon className="w-6 h-6 text-slate-400" />
    </div>
  );
}

// ====================== Signature canvas ======================
function SignatureBlock({ pv, onSigned, onScanUpload }) {
  const [tab, setTab] = useState('digital'); // 'digital' | 'scan'
  const isSigned = pv?.status === 'signed';

  // Form state for digital signature
  const [clientNom, setClientNom] = useState('');
  const [clientFonction, setClientFonction] = useState('');
  const [poseurNom, setPoseurNom] = useState(pv?.nom_prestataire || '');

  const sigClientRef = useRef(null);
  const sigPrestRef = useRef(null);
  const tamponRef = useRef(null);

  return (
    <div className="space-y-4">
      {isSigned ? (
        <SignedView pv={pv} />
      ) : (
        <>
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setTab('digital')}
              className={`px-3 py-1.5 text-sm font-medium ${tab === 'digital' ? 'border-b-2 border-indigo-500 text-indigo-700' : 'text-slate-500'}`}
              data-testid="tab-sign-digital"
            ><PenTool className="w-3.5 h-3.5 inline mr-1" /> Signature digitale</button>
            <button
              onClick={() => setTab('scan')}
              className={`px-3 py-1.5 text-sm font-medium ${tab === 'scan' ? 'border-b-2 border-indigo-500 text-indigo-700' : 'text-slate-500'}`}
              data-testid="tab-sign-scan"
            ><Upload className="w-3.5 h-3.5 inline mr-1" /> Upload PV signé (papier)</button>
          </div>
          {tab === 'digital' ? (
            <DigitalSignForm
              pv={pv}
              clientNom={clientNom} setClientNom={setClientNom}
              clientFonction={clientFonction} setClientFonction={setClientFonction}
              poseurNom={poseurNom} setPoseurNom={setPoseurNom}
              sigClientRef={sigClientRef}
              sigPrestRef={sigPrestRef}
              tamponRef={tamponRef}
              onSigned={onSigned}
            />
          ) : (
            <ScanUploadForm onUpload={onScanUpload} />
          )}
        </>
      )}
    </div>
  );
}

function DigitalSignForm({ pv, clientNom, setClientNom, clientFonction, setClientFonction, poseurNom, setPoseurNom,
  sigClientRef, sigPrestRef, tamponRef, onSigned }) {
  const { token } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const sign = async () => {
    if (!clientNom.trim()) { toast.error('Nom du signataire client obligatoire'); return; }
    if (!sigClientRef.current || sigClientRef.current.isEmpty()) { toast.error('Signature client manquante'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/pv-livraison/${pv.id}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          signataire_client_nom: clientNom,
          signataire_client_fonction: clientFonction,
          signature_client_base64: sigClientRef.current.toDataURL(),
          tampon_client_base64: (tamponRef.current && !tamponRef.current.isEmpty()) ? tamponRef.current.toDataURL() : '',
          poseur_nom: poseurNom,
          signature_prestataire_base64: (sigPrestRef.current && !sigPrestRef.current.isEmpty()) ? sigPrestRef.current.toDataURL() : '',
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Erreur lors de la signature');
        return;
      }
      const updated = await r.json();
      onSigned(updated);
      toast.success('PV signé avec succès ✅');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Client */}
      <div className="border rounded-lg p-3 bg-white space-y-2">
        <p className="text-xs font-bold text-slate-700">Signature CLIENT *</p>
        <Input placeholder="Nom du signataire *" value={clientNom} onChange={e => setClientNom(e.target.value)} data-testid="pv-client-nom" />
        <Input placeholder="Fonction" value={clientFonction} onChange={e => setClientFonction(e.target.value)} data-testid="pv-client-fonction" />
        <SigPad ref={sigClientRef} testid="pv-sig-client" />
      </div>
      {/* Tampon */}
      <div className="border rounded-lg p-3 bg-white space-y-2">
        <p className="text-xs font-bold text-slate-700">Tampon magasin (optionnel)</p>
        <SigPad ref={tamponRef} testid="pv-tampon-client" tall />
      </div>
      {/* Prestataire */}
      <div className="border rounded-lg p-3 bg-white space-y-2">
        <p className="text-xs font-bold text-slate-700">Signature PRESTATAIRE / POSEUR</p>
        <Input placeholder="Nom du poseur" value={poseurNom} onChange={e => setPoseurNom(e.target.value)} data-testid="pv-poseur-nom" />
        <Input value={pv.nom_prestataire || ''} disabled className="bg-slate-50" />
        <SigPad ref={sigPrestRef} testid="pv-sig-prest" />
      </div>
      <div className="lg:col-span-3 flex justify-end">
        <Button onClick={sign} disabled={submitting} className="gap-1 bg-indigo-600 hover:bg-indigo-700" data-testid="pv-sign-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Valider et signer
        </Button>
      </div>
    </div>
  );
}

function ScanUploadForm({ onUpload }) {
  return (
    <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
      <p className="text-sm text-slate-600 mb-3">
        Téléchargez ici le PV signé sur papier (scan ou photo PDF/JPG).
      </p>
      <label className="cursor-pointer">
        <input type="file" accept=".pdf,image/*" className="hidden"
          onChange={e => { if (e.target.files?.[0]) onUpload(e.target.files[0]); e.target.value = ''; }}
          data-testid="pv-scan-upload-input"
        />
        <span className="inline-flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700">
          <Upload className="w-4 h-4" /> Sélectionner un fichier
        </span>
      </label>
    </div>
  );
}

function SignedView({ pv }) {
  const sigClient = pv.signature_client;
  const sigPrest = pv.signature_prestataire;
  return (
    <div className="space-y-3">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
        <div>
          <p className="font-semibold text-emerald-900">PV signé</p>
          <p className="text-xs text-emerald-700">Signé le {pv.signed_at ? new Date(pv.signed_at).toLocaleString('fr-FR') : '—'}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-sm">
        {sigClient && (
          <div className="border rounded p-2 bg-white">
            <p className="text-xs text-slate-500 mb-1">Signature client</p>
            <p className="font-semibold">{sigClient.name}</p>
            {sigClient.function && <p className="text-xs text-slate-600">{sigClient.function}</p>}
            {sigClient.signature_base64 && (
              <img src={sigClient.signature_base64} alt="Signature" className="mt-2 max-h-20 border rounded" />
            )}
          </div>
        )}
        {pv.tampon_client && (
          <div className="border rounded p-2 bg-white">
            <p className="text-xs text-slate-500 mb-1">Tampon</p>
            <img src={pv.tampon_client} alt="Tampon" className="max-h-32 border rounded" />
          </div>
        )}
        {sigPrest && (
          <div className="border rounded p-2 bg-white">
            <p className="text-xs text-slate-500 mb-1">Signature prestataire</p>
            <p className="font-semibold">{sigPrest.name}</p>
            {sigPrest.signature_base64 && (
              <img src={sigPrest.signature_base64} alt="Signature" className="mt-2 max-h-20 border rounded" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Simple signature pad
const SigPad = React.forwardRef(({ testid, tall = false }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const empty = useRef(true);

  React.useImperativeHandle(ref, () => ({
    toDataURL: () => canvasRef.current?.toDataURL('image/png') || '',
    isEmpty: () => empty.current,
    clear: () => clear(),
  }), []);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const evt = e.touches ? e.touches[0] : e;
    return { x: (evt.clientX - r.left) * (canvasRef.current.width / r.width), y: (evt.clientY - r.top) * (canvasRef.current.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    empty.current = false;
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    empty.current = true;
  };

  return (
    <div className="space-y-1">
      <canvas
        ref={canvasRef}
        width={400}
        height={tall ? 180 : 120}
        className={`w-full ${tall ? 'h-32' : 'h-24'} border rounded bg-white touch-none cursor-crosshair`}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid={testid}
      />
      <button onClick={clear} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
        <Eraser className="w-3 h-3" /> Effacer
      </button>
    </div>
  );
});
SigPad.displayName = 'SigPad';
